import {
  onScopeDispose,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import type { SubtitleTrack } from '@/modules/shared/utils/playerSubtitleTracks';
import { downloadCaptionFile, getCaptionDraftList } from '../services';
import type { CaptionDraft } from '../types';
import { captionsChangedVersion } from './captionsChangedSignal';

export type PreviewSubtitleTrack = SubtitleTrack;

const DRAFT_LIST_PAGE_SIZE = 100;

function isPreviewableDraft(draft: CaptionDraft): boolean {
  return draft.publishStatus === 'draft'
    && draft.processingStatus === 'ready'
    && draft.hasContent === true
    && !(Array.isArray(draft.captionContent) && draft.captionContent.length === 0)
    && draft.deleted !== true;
}

async function fetchCaptionDraftPage(videoKey: string, page: number) {
  const response = await getCaptionDraftList(videoKey, { limit: DRAFT_LIST_PAGE_SIZE, page });
  return response.data;
}

async function fetchAllCaptionDrafts(videoKey: string): Promise<CaptionDraft[]> {
  const firstPage = await fetchCaptionDraftPage(videoKey, 1);
  const remainingPageNumbers = Array.from({ length: Math.max(firstPage.lastPage - 1, 0) }, (_, index) => index + 2);
  const remainingPages = await Promise.all(remainingPageNumbers.map((page) => fetchCaptionDraftPage(videoKey, page)));
  return [firstPage, ...remainingPages].flatMap(({ data }) => data);
}

async function downloadWebVtt(draft: CaptionDraft): Promise<string | null> {
  const response = await downloadCaptionFile(draft.key);
  const webVtt: unknown = response.data;
  if (typeof webVtt !== 'string') {
    throw new Error(`Caption draft ${draft.key} download did not return WebVTT text`);
  }
  return webVtt.includes('-->') ? webVtt : null;
}

function revokeTrackUrls(tracks: PreviewSubtitleTrack[]) {
  tracks.forEach(({ src }) => URL.revokeObjectURL(src));
}

function signDrafts(videoKey: string, drafts: CaptionDraft[]): string {
  return JSON.stringify([
    videoKey,
    drafts.map(({
      key, customName, languageCode, updatedAt, captionContent,
    }) => [key, customName, languageCode, updatedAt, captionContent]),
  ]);
}

/**
 * Offers a video's finished draft captions as neutral `{ label, language, src }` subtitle tracks backed by
 * `blob:` URLs it owns. Re-computes when the video key changes or `notifyCaptionsChanged` fires for it, but
 * keeps the current tracks, and so the viewer's selected subtitle, when the previewable drafts are unchanged
 * or the draft list cannot be loaded. A computation in which a download failed is not remembered as current,
 * so the next change retries it.
 */
export function usePreviewSubtitleTracks(
  videoKey: MaybeRefOrGetter<string | null | undefined>,
): { tracks: Readonly<Ref<PreviewSubtitleTrack[]>> } {
  const { t } = useI18n();
  const tracks = shallowRef<PreviewSubtitleTrack[]>([]);
  let appliedSignature: string | null = null;
  let latestRun = 0;

  async function loadPreviewableDrafts(key: string): Promise<CaptionDraft[] | null> {
    try {
      return (await fetchAllCaptionDrafts(key)).filter(isPreviewableDraft);
    } catch (error) {
      console.error('Failed to load caption drafts for preview subtitles', error);
      return null;
    }
  }

  async function buildTracks(drafts: CaptionDraft[]): Promise<{ tracks: PreviewSubtitleTrack[]; complete: boolean }> {
    const results = await Promise.allSettled(drafts.map(downloadWebVtt));
    let complete = true;
    const builtTracks = results.flatMap((result, index) => {
      const draft = drafts[index];
      if (result.status === 'rejected') {
        console.error(`Failed to download caption draft ${draft.key} for preview subtitles`, result.reason);
        complete = false;
        return [];
      }
      if (result.value === null) {
        return [];
      }
      return [{
        label: `${draft.customName} ${t('videoSubtitle.draftMarker')}`,
        language: draft.languageCode,
        src: URL.createObjectURL(new Blob([result.value], { type: 'text/vtt' })),
      }];
    });
    return { tracks: builtTracks, complete };
  }

  function applyTracks(nextTracks: PreviewSubtitleTrack[], signature: string | null) {
    const previousTracks = tracks.value;
    tracks.value = nextTracks;
    appliedSignature = signature;
    revokeTrackUrls(previousTracks);
  }

  async function recompute(key: string | null | undefined) {
    latestRun += 1;
    const run = latestRun;
    if (!key) {
      applyTracks([], null);
      return;
    }

    const drafts = await loadPreviewableDrafts(key);
    if (run !== latestRun || drafts === null) {
      return;
    }
    const signature = signDrafts(key, drafts);
    if (signature === appliedSignature) {
      return;
    }

    const built = await buildTracks(drafts);
    if (run !== latestRun) {
      revokeTrackUrls(built.tracks);
      return;
    }
    applyTracks(built.tracks, built.complete ? signature : null);
  }

  watch(
    [
      () => toValue(videoKey),
      () => {
        const key = toValue(videoKey);
        return key ? captionsChangedVersion(key) : 0;
      },
    ],
    ([key]) => {
      recompute(key);
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    latestRun += 1;
    revokeTrackUrls(tracks.value);
    tracks.value = [];
  });

  return { tracks };
}
